// dropNamedDefault — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'drop-named-default', constraintName, standalone })`
// op (DSE2 §3.B5).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B5.
//
// mssql-only. Two synthesizer shapes:
//   - inline (default): `ALTER TABLE … DROP CONSTRAINT df_name`. Requires a
//     CREATE/ALTER TABLE source statement so the synthesizer can target the
//     right table.
//   - standalone (`standalone: true`): `DROP DEFAULT df_name`. Drops a free-
//     floating DEFAULT created via the deprecated `CREATE DEFAULT` form. No
//     source-statement table required.
//
// Other engines reject — see `addNamedDefault.ts` for the redirect.

import type { DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

import { getAddNamedDefaultUnsupportedTooltip } from './addNamedDefault';

export type DropNamedDefaultArgs = {
  readonly constraintName: string;
  readonly standalone?: boolean;
};

export function buildDropNamedDefaultEdit(args: DropNamedDefaultArgs): DdlEdit {
  return {
    op: 'drop-named-default',
    constraintName: args.constraintName,
    standalone: args.standalone,
  };
}

export async function commitDropNamedDefault(
  ctx: VqeDdlEditContext,
  args: DropNamedDefaultArgs,
): Promise<void> {
  const edit = buildDropNamedDefaultEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isDropNamedDefaultSupported(engine: SqlEngine): boolean {
  return engine === 'mssql';
}

export function getDropNamedDefaultUnsupportedTooltip(engine: SqlEngine): string {
  return getAddNamedDefaultUnsupportedTooltip(engine);
}

/** Pre-flight validator mirroring DSE2's drop rules. */
export function validateDropNamedDefaultForEngine(
  engine: SqlEngine,
  args: DropNamedDefaultArgs,
): string | null {
  if (engine !== 'mssql') return getDropNamedDefaultUnsupportedTooltip(engine);
  if (!args.constraintName || args.constraintName.trim().length === 0) {
    return 'Drop named DEFAULT requires a constraint name.';
  }
  return null;
}
